/**
 * The pattern editor for one type in one type list — Certainty, Shape,
 * Workload, Bar (docs/9-exploration/TYPE-PATTERNS-PRD.md §3.1). Saving writes a
 * new version through revise_type_pattern; earlier versions stay, and a plan
 * that read one keeps reading it.
 *
 * Used by Settings → Type lists & patterns and by the Launch Types and
 * Investment Types pages, so every list edits its patterns the same way.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { Field, Select, TextArea, TextInput } from '../../ds';
import { btnPrimary, btnSecondary } from '../../mono/primitives';
import type { TypeListWithOptions } from '../../../hooks/prioritization/useTypeListPickers';
import type { LaunchProfile } from '../../../lib/timePhasedPlanning.types';
import {
  ANCHOR_LABEL,
  DATE_FIRMNESS,
  DATE_FIRMNESS_LABEL,
  HOURS_PER_CU,
  QUALITY_BARS,
  QUALITY_LABEL,
  defaultAnchorFor,
} from '../../../lib/typePatterns';
import { reviseTypePattern } from '../../../lib/typePatternsService';
import {
  EMPTY_PATTERN,
  type PatternAnchor,
  type PatternParts,
  type TypeOption,
  type TypePattern,
} from '../../../types/prioritization/typePatterns';

export function PatternEditor({
  list,
  type,
  current,
  workTypeFacts,
  resourceTypes,
  workloadTemplates,
  onSaved,
  onCancel,
}: {
  list: TypeListWithOptions;
  type: TypeOption;
  current: TypePattern | null;
  workTypeFacts: { approach?: string | null; effortMultiplier?: number | null } | null;
  resourceTypes: Array<{ key: string; label: string }>;
  workloadTemplates: LaunchProfile[];
  onSaved: (p: TypePattern) => void;
  onCancel: () => void;
}) {
  const start: PatternParts = current ?? EMPTY_PATTERN;
  const defAnchor = defaultAnchorFor(list.key);
  const txt = (n: number | undefined) => (n === undefined ? '' : String(n));

  // Everything is kept as typed ("-6", "1.5", "Launch brief approved") and
  // converted once, on save.
  const [slack, setSlack] = useState(txt(start.certainty.scheduleSlackWeeks));
  const [confidence, setConfidence] = useState(
    start.certainty.confidenceCeiling === undefined ? '' : String(Math.round(start.certainty.confidenceCeiling * 100)),
  );
  const [before, setBefore] = useState((start.certainty.beforeScheduling ?? []).join('\n'));
  const [shapeAnchor, setShapeAnchor] = useState<PatternAnchor>(start.shape.anchor ?? defAnchor);
  const [stages, setStages] = useState((start.shape.stages ?? []).map((s) => ({ label: s.label, weeks: String(s.weeks) })));
  const [workAnchor, setWorkAnchor] = useState<PatternAnchor>(start.workload.anchor ?? defAnchor);
  const [lines, setLines] = useState(
    (start.workload.lines ?? []).map((l) => ({
      resourceType: l.resourceType,
      cu: String(l.capacityUnits),
      from: String(l.fromWeek),
      to: String(l.toWeek),
    })),
  );
  const [date, setDate] = useState(start.bar.date ?? '');
  const [quality, setQuality] = useState(start.bar.quality ?? '');
  const [firmness, setFirmness] = useState(txt(start.bar.firmness));
  const [signOff, setSignOff] = useState((start.bar.signOff ?? []).join('\n'));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const num = (v: string): number | undefined => {
    const n = Number(v.trim());
    return v.trim() === '' || !Number.isFinite(n) ? undefined : n;
  };
  const split = (v: string) => v.split('\n').map((s) => s.trim()).filter(Boolean);

  function build(): { parts: PatternParts; problem: string | null } {
    const badLine = lines.find((l) => num(l.cu) === undefined || num(l.from) === undefined || num(l.to) === undefined);
    if (badLine) return { parts: EMPTY_PATTERN, problem: 'Each team needs capacity units and both weeks as numbers.' };
    const backwards = lines.find((l) => (num(l.to) ?? 0) < (num(l.from) ?? 0));
    if (backwards) return { parts: EMPTY_PATTERN, problem: `${backwards.resourceType}: the last week comes before the first.` };
    const conf = num(confidence);
    const firm = num(firmness);
    return {
      problem: null,
      parts: {
        certainty: {
          scheduleSlackWeeks: num(slack),
          confidenceCeiling: conf === undefined ? undefined : Math.min(100, Math.max(0, conf)) / 100,
          beforeScheduling: split(before),
        },
        shape: {
          anchor: shapeAnchor,
          stages: stages
            .map((s) => ({ label: s.label.trim(), weeks: num(s.weeks) ?? 0 }))
            .filter((s) => s.label && s.weeks > 0),
        },
        workload: {
          anchor: workAnchor,
          lines: lines.map((l) => ({
            resourceType: l.resourceType,
            capacityUnits: num(l.cu) ?? 0,
            fromWeek: Math.round(num(l.from) ?? 0),
            toWeek: Math.round(num(l.to) ?? 0),
          })),
        },
        bar: {
          date: (date || undefined) as PatternParts['bar']['date'],
          quality: (quality || undefined) as PatternParts['bar']['quality'],
          firmness: firm === undefined ? undefined : Math.min(10, Math.max(1, Math.round(firm))),
          signOff: split(signOff),
        },
      },
    };
  }

  async function save() {
    const { parts, problem } = build();
    if (problem) return setError(problem);
    setSaving(true);
    setError(null);
    const { pattern, error: err } = await reviseTypePattern({
      workspaceId: list.workspaceId,
      listKey: list.key,
      typeValue: type.value,
      parts,
      reason: reason.trim() || null,
    });
    setSaving(false);
    if (err || !pattern) {
      setError(err ?? 'Nothing was saved.');
      return;
    }
    onSaved(pattern);
  }

  function copyTemplate(id: string) {
    const t = workloadTemplates.find((x) => x.id === id);
    if (!t) return;
    setWorkAnchor('date');
    setLines(
      t.phases.map((ph) => ({
        resourceType: ph.resourceType,
        cu: String(Math.round((ph.hours / HOURS_PER_CU) * 10) / 10),
        from: String(Math.round(ph.offsetStartWeeks)),
        // A template phase ends at the start of the week after; a line's weeks are inclusive.
        to: String(Math.max(Math.round(ph.offsetStartWeeks), Math.round(ph.offsetEndWeeks) - 1)),
      })),
    );
  }

  const anchorOptions = (['start', 'date'] as PatternAnchor[]).map((a) => ({ value: a, label: ANCHOR_LABEL[a] }));
  const teamOptions = (current: string) =>
    [...resourceTypes, ...(resourceTypes.some((r) => r.key === current) ? [] : [{ key: current, label: current }])].map((r) => ({
      value: r.key,
      label: r.label,
    }));

  return (
    <div className="mt-3 space-y-6 border border-[var(--rule)] bg-[var(--surface)] p-4" data-testid="pattern-editor">
      <p className="text-sm text-[var(--text-sec)]">
        Pattern for <strong className="text-[var(--text-pri)]">{type.label}</strong>
        {current ? ` · version ${current.versionNumber}` : ' · first version'}. Saving keeps earlier
        versions; a plan that read one keeps reading it.
      </p>

      <section>
        <h4 className="text-sm font-semibold text-[var(--text-pri)]">Certainty — how sure are we?</h4>
        {list.key === 'work_type' && (
          <p className="mt-1 text-xs text-[var(--text-ter)]">
            Approach {workTypeFacts?.approach ?? '—'} · effort ×{workTypeFacts?.effortMultiplier ?? 1}. These
            two live on the work type itself — change them on{' '}
            <Link to="/prioritization/settings/work-types" className="underline">Work Types</Link>.
          </p>
        )}
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <Field label="Schedule slack (weeks)" hint="How far a window may slip before the slip counts as hard.">
            <TextInput size="sm" inputMode="decimal" value={slack} onChange={(e) => setSlack(e.target.value)} />
          </Field>
          <Field label="Highest confidence (%)" hint="The most calendar confidence work of this type can carry.">
            <TextInput size="sm" inputMode="decimal" value={confidence} onChange={(e) => setConfidence(e.target.value)} />
          </Field>
        </div>
        <div className="mt-3">
          <Field label="Before scheduling (one per line)" hint="What must be true first, e.g. a design document, kill criteria.">
            <TextArea rows={2} value={before} onChange={(e) => setBefore(e.target.value)} />
          </Field>
        </div>
      </section>

      <section>
        <h4 className="text-sm font-semibold text-[var(--text-pri)]">Shape — what usually happens, in order</h4>
        <div className="mt-2 max-w-xs">
          <Field label="Hangs off">
            <Select size="sm" value={shapeAnchor} onChange={(e) => setShapeAnchor(e.target.value as PatternAnchor)} options={anchorOptions} />
          </Field>
        </div>
        <div className="mt-2 space-y-2">
          {stages.map((s, i) => (
            <div key={i} className="flex items-end gap-2">
              <div className="flex-1">
                <TextInput size="sm" aria-label={`Stage ${i + 1}`} value={s.label} placeholder="e.g. Discovery"
                  onChange={(e) => setStages(stages.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              </div>
              <div className="w-24">
                <TextInput size="sm" aria-label={`Stage ${i + 1} weeks`} inputMode="decimal" value={s.weeks}
                  onChange={(e) => setStages(stages.map((x, j) => (j === i ? { ...x, weeks: e.target.value } : x)))} />
              </div>
              <span className="pb-2 text-xs text-[var(--text-ter)]">weeks</span>
              <button type="button" aria-label={`Remove stage ${i + 1}`} className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)]"
                onClick={() => setStages(stages.filter((_, j) => j !== i))}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button type="button" {...btnSecondary('flex items-center gap-1 text-xs')}
            onClick={() => setStages([...stages, { label: '', weeks: '1' }])}>
            <Plus className="h-3 w-3" /> Add stage
          </button>
        </div>
      </section>

      <section>
        <h4 className="text-sm font-semibold text-[var(--text-pri)]">Workload — which teams, how much, when</h4>
        <p className="mt-1 text-xs text-[var(--text-ter)]">
          Capacity units (1 CU ≈ {HOURS_PER_CU} hours). Weeks count from what it hangs off; negative weeks are before it.
          The rough cut reads the totals; the launch plan spreads them over these weeks.
        </p>
        <div className="mt-2 flex flex-wrap items-end gap-3">
          <div className="w-56">
            <Field label="Hangs off">
              <Select size="sm" value={workAnchor} onChange={(e) => setWorkAnchor(e.target.value as PatternAnchor)} options={anchorOptions} />
            </Field>
          </div>
          {workloadTemplates.length > 0 && (
            <div className="w-64">
              <Field label="Start from a workload template">
                <Select size="sm" value="" placeholder="Choose…" onChange={(e) => copyTemplate(e.target.value)}
                  options={workloadTemplates.map((t) => ({ value: t.id, label: t.name }))} />
              </Field>
            </div>
          )}
        </div>
        <div className="mt-2 space-y-2">
          {lines.map((l, i) => {
            const upd = (patch: Partial<typeof l>) => setLines(lines.map((x, j) => (j === i ? { ...x, ...patch } : x)));
            return (
              <div key={i} className="flex flex-wrap items-end gap-2">
                <div className="w-44">
                  <Select size="sm" aria-label={`Team ${i + 1}`} value={l.resourceType} onChange={(e) => upd({ resourceType: e.target.value })}
                    options={teamOptions(l.resourceType)} />
                </div>
                <div className="w-20"><TextInput size="sm" aria-label={`Team ${i + 1} capacity units`} inputMode="decimal" value={l.cu} onChange={(e) => upd({ cu: e.target.value })} /></div>
                <span className="pb-2 text-xs text-[var(--text-ter)]">CU, weeks</span>
                <div className="w-16"><TextInput size="sm" aria-label={`Team ${i + 1} first week`} value={l.from} onChange={(e) => upd({ from: e.target.value })} /></div>
                <span className="pb-2 text-xs text-[var(--text-ter)]">to</span>
                <div className="w-16"><TextInput size="sm" aria-label={`Team ${i + 1} last week`} value={l.to} onChange={(e) => upd({ to: e.target.value })} /></div>
                <button type="button" aria-label={`Remove team ${i + 1}`} className="p-2 text-[var(--text-ter)] hover:text-[var(--coral)]"
                  onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
          <button type="button" {...btnSecondary('flex items-center gap-1 text-xs')}
            onClick={() => setLines([...lines, { resourceType: resourceTypes[0]?.key ?? 'engineering', cu: '1', from: '0', to: '0' }])}>
            <Plus className="h-3 w-3" /> Add team
          </button>
        </div>
      </section>

      <section>
        <h4 className="text-sm font-semibold text-[var(--text-pri)]">Bar — what it is held to</h4>
        <div className="mt-2 grid gap-3 sm:grid-cols-3">
          <Field label="Date">
            <Select size="sm" value={date} placeholder="Not stated" onChange={(e) => setDate(e.target.value)}
              options={DATE_FIRMNESS.map((d) => ({ value: d, label: DATE_FIRMNESS_LABEL[d] }))} />
          </Field>
          <Field label="Quality bar">
            <Select size="sm" value={quality} placeholder="Not stated" onChange={(e) => setQuality(e.target.value)}
              options={QUALITY_BARS.map((q) => ({ value: q, label: QUALITY_LABEL[q] }))} />
          </Field>
          <Field label="Slides last (1–10)" hint="Higher holds its place when calendars collide.">
            <TextInput size="sm" inputMode="numeric" value={firmness} onChange={(e) => setFirmness(e.target.value)} />
          </Field>
        </div>
        <div className="mt-3">
          <Field label="Signs off (one per line)">
            <TextArea rows={2} value={signOff} onChange={(e) => setSignOff(e.target.value)} />
          </Field>
        </div>
      </section>

      <Field label="Why this change (optional)">
        <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. conference launches need six weeks of marketing" />
      </Field>

      {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} {...btnSecondary()}>Cancel</button>
        <button type="button" disabled={saving} onClick={() => void save()} {...btnPrimary('disabled:opacity-50')}>
          {saving ? 'Saving…' : 'Save pattern'}
        </button>
      </div>
    </div>
  );
}

